import * as requestFromServer from "./authApis";
import Actions from "../../actions";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../constants/routeConsts";
import { toastService } from "../../../component/common/toast-messages/toast";
import { userRoles } from "../../../constants/userRolesConts";

// country -> api
export const getCountryAction = () => async (dispatch) => {
  const response = await requestFromServer.getAllCountry();
  if (response) {
    dispatch(
      Actions.createAction(Actions.PROVIDER_COUNTRY, {
        countries: response,
      })
    );
    return response;
  }
};
// state -> api
export const getStateAction = (stateObject) => async (dispatch) => {
  const response = await requestFromServer.getStateByCountry(stateObject);
  if (response) {
    dispatch(
      Actions.createAction(Actions.PROVIDER_STATE, {
        states: response?.[0]?.states,
      })
    );

    return response;
  }
};

// city -> api
export const getCityAction = (cityBody) => async (dispatch) => {
  const response = await requestFromServer.getCityByState(cityBody);
  if (response) {
    dispatch(Actions.createAction(Actions.PROVIDER_CITY, { cities: response }));
    return response;
  }
};

// register -> api
export const createServiceProvider =
  (registerObject, navigate, setLoading) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.register(registerObject);
    if (response) {
      toastService.success(response.message);
      dispatch(
        Actions.createAction(Actions.PROVIDER_REGISTER_DETAILS, registerObject)
      );
      navigate(baseRouteMaker(routes.OTP_VERIFICATION_PHONE));
      return response;
    }
    setLoading(false);
  };

// otp-phone -> api
export const verifyPhone =
  (otpBody, navigate, setLoading) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.verifyPhoneOtp(otpBody);
    if (response) {
      toastService.success(response?.message);
      dispatch(Actions.createAction(Actions.PROVIDER_VERIFY_PHONE, otpBody));
      navigate(baseRouteMaker(routes.OTP_VERIFICATION_EMAIL));
      return response;
    }
    setLoading(false);
  };

// otp-email -> api
export const verifyEmail =
  (otpBody, navigate, setLoading, setUserToken) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.verifyEmailOtp(otpBody);

    if (response) {
      toastService.success(response?.message);
      /* setting the user token in the session */
      setUserToken(response?.token);
      /* setting the user token in the session */

      dispatch(Actions.createAction(Actions.PROVIDER_VERIFY_EMAIL, otpBody));
      navigate(baseRouteMaker(routes.SET_NEW_PASSWORD));
      return response;
    }
    setLoading(false);
  };

// set-new-password -> api
export const setNewProviderPassword =
  (setPasswordBody, navigate, setLoading, setUserToken) => async (dispatch) => {
      setLoading(true);
    const response = await requestFromServer.setNewPassword(setPasswordBody);

    if (response) {
      /* setting the user token in the session */
      setUserToken(response);
      /* setting the user token in the session */

      toastService.success(response?.message);
      dispatch(Actions.createAction(Actions.PROVIDER_SET_NEW_PASSWORD, setPasswordBody));
      navigate(baseRouteMaker(routes.PROFILE_SETUP));
      return response;
    }
    setLoading(false);
  };

// set-provider-profile -> api
export const setupProfile =
  (profileBody, navigate, setLoading, setUserSession) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.setupNewProfile(profileBody);
    if (response) {
      toastService.success(response?.message);
      dispatch(
        Actions.createAction(
          Actions.PROVIDER_SET_UP_PROFILE_BANK_ACCOUNT,
          profileBody
        )
      );

      /* setting the user session based on roles */
      dispatch(getUserProfileAction(setUserSession)).then(() => {
        navigate(baseRouteMaker(routes.DASHBOARD));
      });
      /* setting the user session based on roles */

      return response;
    }
    setLoading(false);
  };

// update-provider-profile -> api
export const updateProviderProfileAction =
  (profileBody, navigate, setLoading, setUserSession) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.updateProviderProfile(profileBody);
    if (response) {
      toastService.success(response?.message);

      /* setting the user session based on roles */
      dispatch(getUserProfileAction(setUserSession)).then(() => {
        navigate(baseRouteMaker(routes.DASHBOARD));
      });
      /* setting the user session based on roles */

      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

// update-provider-bank-details -> api
export const updateBankDetailsAction =
  (dataToBeSent, navigate, setLoading, setUserSession) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.updateBankDetails(dataToBeSent);
    if (response) {
      toastService.success(response?.message);

      /* setting the user session based on roles */
      dispatch(getUserProfileAction(setUserSession)).then(() => {
        navigate(baseRouteMaker(routes.DASHBOARD));
      });
      /* setting the user session based on roles */

      navigate(baseRouteMaker(routes.DASHBOARD));
      return response;
    }
    setLoading(false);
  };

// update-provider-bank-details -> api
export const updatePaymentMethod =
  (dataToBeSent, setLoading) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.updateBankDetails(dataToBeSent);
    if (response) {
      toastService.success(response?.message);
      return response;
    }
    setLoading(false);
  };

// update-provider-profile -> api
export const updateStaffProfileAction =
  (profileBody, navigate, setLoading, setUserSession) => async (dispatch) => {
    try {
      setLoading(false);
      const response = await requestFromServer.updateStaffProfile(profileBody);
      if (response.status === 200 || response.status === 201) {
        toastService.success(response?.message);
        setLoading(false);

        /* setting the user session based on roles */
        dispatch(getUserProfileAction(setUserSession)).then(() => {
          navigate(baseRouteMaker(routes.DASHBOARD));
        });
        /* setting the user session based on roles */

        return response;
      } else {
        setLoading(false);
        return null;
      }
    } catch (err) {
      setLoading(false);
      toastService.error(err?.response?.data?.message);
    }
  };

// get-provider-profile -> api
export const getUserProfileAction =
  (setUserSession, updateLanguage) => async (dispatch) => {
    const response = await requestFromServer.getUserProfile();
    if (response) {
      const userDetails = response;
      const userRole = userDetails?.role;
      let userName, userEmail, userProfileImage, userId, userPermissions;

      localStorage.setItem("i18nextLng", userDetails.preferred_language);
      updateLanguage && updateLanguage(userDetails.preferred_language);
      switch (userRole) {
        case userRoles.SERVICE_PROVIDER_ADMIN:
          userName = userDetails?.organization_id?.name;
          userEmail = userDetails?.email;
          userProfileImage = userDetails?.organization_id?.logo_image;
          userId = userDetails?.organization_id?.owner;
          userPermissions = userDetails?.organization_id?.permissions;
          break;

        case userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR:
          userName = `${userDetails?.first_name} ${userDetails?.last_name}`;
          userEmail = userDetails?.email;
          userProfileImage = userDetails?.profile_image;
          userId = userDetails?._id;
          userPermissions = userDetails.organization_id.permissions;
          break;

        case userRoles.SUPER_ADMIN:
          userName = "Super Admin";
          userEmail = userDetails?.email;
          userProfileImage = userDetails?.profile_image;
          userId = userDetails?._id;
          userPermissions = userDetails.organization_id.permissions;
          break;

        case userRoles.CUSTOMER:
          // toastService.error("You are not authorized to login");
          // localStorage.clear();
          dispatch(Actions.createAction(Actions.USER_CONVERSION_DIALOG_OPEN));
          break;

        default:
          userName = `${userDetails?.first_name} ${userDetails?.last_name}`;
          userEmail = userDetails?.email;
          userProfileImage = userDetails?.profile_image;
          userId = userDetails?._id;
          userPermissions = userDetails.organization_id.permissions;
          break;
      }

      const dataToBeSent = {
        userName,
        userEmail,
        userProfileImage,
        userRole,
        userId,
        userPermissions,
      };
      dispatch(
        Actions.createAction(Actions.PROVIDER_LOGIN, {
          loginDetails: userDetails,
        })
      );
      setUserSession(dataToBeSent);

      return response;
    } else {
      return null;
    }
  };

// login -> api
export const login =
  (
    loginBody,
    navigate,
    setLoading,
    setUserToken,
    setUserSession,
    updateLanguage
  ) =>
  async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.providerLogin(loginBody);
    if (response) {
      /* setting the user token in the session */
      setUserToken(response?.token);
      /* setting the user token in the session */

      dispatch(getUserProfileAction(setUserSession, updateLanguage)).then(
        (apiRes) => {
          const resp = apiRes;

          switch (resp?.role) {
            case userRoles.SUPER_ADMIN:
              navigate(
                baseRouteMakerForSuperAdmin(routes.SUPER_ADMIN_DASHBOARD)
              );
              break;

            case userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF:
              navigate(baseRouteMaker(routes.STAFF_DASHBOARD));
              break;

            case userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF:
              navigate(baseRouteMaker(routes.STAFF_DASHBOARD));
              break;

            case userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR:
              navigate(baseRouteMaker(routes.STAFF_DASHBOARD));
              break;

            case userRoles.SERVICE_PROVIDER_ADMIN:
              navigate(baseRouteMaker(routes.DASHBOARD));
              break;

            case userRoles.SUPER_ADMIN_ROLES.ACCOUNTANT:
              navigate(baseRouteMakerForSuperAdmin(routes.FUND_MANAGEMENT));
              break;

            case userRoles.SUPER_ADMIN_ROLES.CUSTOMER_SUPPORT:
              navigate(baseRouteMakerForSuperAdmin(routes.TICKET_MANAGEMENT));
              break;

            case userRoles.CUSTOMER: {
              dispatch(
                Actions.createAction(Actions.USER_CONVERSION_DIALOG_OPEN)
              );
              setLoading(false);
              return;
            }

            default:
              break;
          }

          toastService.success(response?.message);
        }
      );

      setLoading(false);
      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

// forgot-password -> api
export const forgotPassword =
  (forgotPasswordBody, navigate, setLoading) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.forgotPassword(forgotPasswordBody);
    if (response) {
      toastService.success(response?.message);
      dispatch(
        Actions.createAction(
          Actions.PROVIDER_FORGOT_PASSWORD,
          forgotPasswordBody
        )
      );
      navigate(baseRouteMaker(routes.FORGOT_PASSWORD_VERIFICATION));
      setLoading(false);
      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

// verify-forgot-password -> api
export const verifyForgotPassword =
  (forgotPasswordBody, navigate, setLoading, setUserToken) =>
  async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.verifyForgotPassword(
      forgotPasswordBody
    );
    if (response) {
      toastService.success(response?.message);
      /* setting the user token in the session */
      setUserToken(response?.token);
      /* setting the user token in the session */
      setLoading(false);
      dispatch(
        Actions.createAction(
          Actions.PROVIDER_FORGOT_PASSWORD_VERIFICATION,
          forgotPasswordBody
        )
      );
      navigate(baseRouteMaker(routes.RESET_PASSWORD));
      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

// reset-new-password -> api
export const resetNewPassword =
  (resetPasswordBody, navigate, setLoading) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.resetNewPassword(
      resetPasswordBody
    );
    if (response) {
      setLoading(false);
      toastService.success(response?.message);
      dispatch(
        Actions.createAction(Actions.PROVIDER_RESET_PASSWORD, resetPasswordBody)
      );
      localStorage.clear();
      navigate(baseRouteMaker(routes.SIGN_IN));
      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

// resend-otp-phone -> api
export const resendVerifyPhone =
  (resendBody, setIsLoading) => async (dispatch) => {
    setIsLoading(true);
    const response = await requestFromServer.resendVerifyPhoneOtp(resendBody);
    if (response) {
      toastService.success(response?.message);
      dispatch(
        Actions.createAction(Actions.PROVIDER_RESEND_OTP_PHONE, resendBody)
      );
      setIsLoading(false);
      return response;
    } else {
      setIsLoading(false);
      return null;
    }
  };

// resend-otp-email -> api
export const resendVerifyEmail =
  (resendBody, setLoading, setUserToken) => async (dispatch) => {
    setLoading(true);
    const response = await requestFromServer.resendVerifyEmailOtp(resendBody);
    if (response) {
      toastService.success(response?.message);
      dispatch(
        Actions.createAction(Actions.PROVIDER_RESEND_OTP_EMAIL, resendBody)
      );
      setLoading(false);
      return response;
    } else {
      setLoading(false);
      return null;
    }
  };

export const fileImageUpload = (formDataBody) => async (dispatch) => {
  const response = await requestFromServer.fileImageUpload(formDataBody);
  return response;
};

export const fileDocumentUploadAction = (formDataBody) => async (dispatch) => {
  const response = await requestFromServer.fileDocumentUpload(formDataBody);
  return response;
};

export const fileUploadAction = (formDataBody) => async (dispatch) => {
  const response = await requestFromServer.fileUpload(formDataBody);
  return response;
};

export const saveFcm = async (requestData) => {
  const token = localStorage.getItem("FCM_TOKEN");
  if (token) {
    try {
      const response = await requestFromServer.saveFCM(requestData);
      if (response.statusCode === 200) {
        return response;
      } else {
        console.log("error", response.data);

        return null;
      }
    } catch (error) {
      console.log("saveFCMError:-", error);
    }
  }
};
